import { HisColumnMetadata } from './his-column-metadata';
import { HisOption } from './his-option';

/**
 * The result of a search operation as delivered by the server.
 */
export class RestSearchResult {
    name: string;
    cacheVersion?: number;
    columns: HisColumnMetadata[];
    // the index of the first result row to return, starting at 0
    offset: number;
    // the actual number of returned result rows
    limit: number;
    // a hard limit on the number of result rows to return at once imposed by search configuration
    maxResultRows: number;
    // the total number of search results
    totalCount: number;
    data: any[][];
}

/** Defines API for selectable rows in a search result table. */
export interface SelectableRow {
    /**
     * Uniquely identifies a search result row accross different implemetations.
     * 
     * Must return the same value for all implementations referencing the same actual row, regardless of its additional data.
     * 
     * @devNote For now the intended identifier of a row is the related `searchEntityId`, this might be expanded to include `hiddenSelects` in the future.
     */
    readonly rowKey: string;
}

/** Implements common functionality for selectable row elements. */
abstract class AbstractSelectableRow implements SelectableRow {
    abstract readonly rowKey: string;

    /** Compares this row with another selectable row reference to determine if they reference the same row. */
    public equals( other: SelectableRow | string ) {
        return this.rowKey === ( typeof other === 'string' ? other : other.rowKey );
    }
}

export class HisSearchResultRow extends AbstractSelectableRow {
    public values: any[];
    metadata: HisColumnMetadata[];
    idIdx: number;
    labelIdx: number;
    /** Unique identifier for a row entry, used to track its selection.
     * @devNote must be a 'real' property for primeNg to be able to use as `dataKey`. */
    rowKey: string;

    constructor( values: any[], metadata: HisColumnMetadata[], idIdx?: number, labelIdx = 0 ) {
        super();
        this.values = values;
        this.metadata = metadata;

        // by default, the first hidden column containes the searchEntityId
        this.idIdx = idIdx ?? metadata.findIndex( colMeta => !colMeta.visible );
        // by default, the first column is used as the fallback label column
        this.labelIdx = labelIdx;
        // String() is necessary to ensure correct typing at runtime
        this.rowKey = String( values[this.idIdx] );
    }

    /**
     * Get the index of the column with the given id.
     *
     * @param columnId the column id
     * @return the index of the column with the gi
     */
    public getIndex( columnId: string ): number {
        for ( let i = 0; i < this.metadata.length; i++ ) {
            if ( this.metadata[i].id === columnId ) {
                return i;
            }
        }
        // Use the following line only for debugging. In general, we don't want an error message because columns may be added lazely.
        // console.error( 'could not find row id \'' + id + '\' in the current search metadata', this.metadata );
        return null;
    }

    /**
     * Get the value of this row at the given column index.
     *
     * @param columnIndex the column index
     * @result the value at the given column index
     */
    public getAt( columnIndex: number ): any {
        return this.values[columnIndex];
    }

    /**
     * Get the value of this row's column with the given columnId.
     *
     * @param columnId the column id
     * @return the value of the column, or null if the columnId is not known.
     */
    public get( columnId: string ): any {
        let columnIndex = this.getIndex( columnId );
        return columnIndex !== null ? this.values[columnIndex] : null;
    }

    /**
     * Set the value of the column with id 'columnId' to 'value'.
     *
     * @param columnId the column id
     * @param value the new value to set
     */
    public set( columnId: string, value: any, theSortPath?: string, theLabel?: string,
        theDataType?: string, theFormat?: string, theFilterPath?: string ) {
        let columnIndex = this.getIndex( columnId );
        if ( columnIndex !== null ) {
            this.values[columnIndex] = value;
        } else { // new column!
            this.metadata.push( {
                id: columnId, label: theLabel, dataType: theDataType, format: theFormat, width: null, visible: true,
                sortPath: theSortPath, filterPath: theFilterPath
            } );
            this.values.push( value );
        }
    }

    /**
     * Find the column id containing the given substring.
     *
     * @param substr the string to search for
     * @return the id containing 'substr'
     */
    findIdByContent( substr: string ): string {
        let foundId = null;
        this.metadata.forEach( ( rowMetadata: HisColumnMetadata ) => {
            if ( rowMetadata.id.includes( substr ) ) {
                foundId = rowMetadata.id;
            }
        } );
        return foundId;
    }

    toFlatSearchResultRow( theId: string, theLabel: string ): any {
        let flatRow: any & { id: string; label: string } = { id: theId, label: theLabel };
        let i = 0;
        for ( let columnMeta of this.metadata ) {
            let value = this.values[i];
            if ( value ) {
                flatRow[columnMeta.id] = value;
            } // else we dont need to set it, requesting a non-existing attribute will give undefined, too
            i++;
        }
        return flatRow;
    }

    /**
     * Convert this row to a {@linkplain HisSearchResultSelectedRow} for selection handling.
     */
    toHisSearchResultSelectedRow( customIdx: { id?: number, label?: number } = {} ) {
        const id = this.values[customIdx.id ?? this.idIdx];
        const label = this.values[customIdx.label ?? this.labelIdx];
        return new HisSearchResultSelectedRow( id, id, label, this );
    }
}

/**
 * Representation of a selected `HisSearchResultRow`.
 * 
 * Conforms to the type `HisOption` for subsequent use, for example in the job-starter or search-select.
 * 
 * Defines a `rowKey` to uniquely identify this row for accurate selection accross the whole result set.
 */
export class HisSearchResultSelectedRow extends AbstractSelectableRow implements HisOption {
    /** searchEntityId (as string to satisfy HisOption) */
    public readonly id: string;
    /** label to namely mention/display the row/option */
    public readonly label: string;
    /** the actual result row behind this reference, if available */
    public readonly actualRow?: HisSearchResultRow;
    /** identifier for a row */
    public readonly rowKey: string;
    /** additional data for local requirements of implementing components that go beyond a HisOption or the actual row data */
    public customData: Record<string, any> = {};

    constructor( rowKey: string, id: string, label: string, actualRow?: HisSearchResultRow ) {
        super();
        this.id = id;
        this.label = label;
        // String() is necessary to ensure correct typing at runtime
        this.rowKey = String( rowKey );
        this.actualRow = actualRow;
    }

    public toFlatHisOption(): HisOption {
        const result: any = { id: this.id, label: this.label, rowKey: this.rowKey }; // HisOption object
        if ( this.actualRow?.metadata ) {
            this.actualRow.metadata.forEach( ( col, idx ) => {
                result[col.id] = this.actualRow.values[idx];
            } );
        }
        if ( this.customData ) {
            for ( const key in this.customData ) {
                const value = this.customData[key];
                result[key] = typeof value === 'string' ? value : JSON.stringify( value );
            }
        }
        return result;
    }
}

/**
 * The result of a search operation.
 */
export class HisSearchResult {
    name: string;
    cacheVersion?: number;
    columns: HisColumnMetadata[];
    data: HisSearchResultRow[];
    // the index of the first result row to return, starting at 0
    offset: number;
    // the actual number of returned result rows
    limit: number;
    // a hard limit on the number of result rows to return at once imposed by search configuration
    maxResultRows: number;
    // the total number of search results
    totalCount: number;

    constructor( from: RestSearchResult ) {
        this.name = from.name;
        if ( from.cacheVersion ) { this.cacheVersion = from.cacheVersion; }
        this.columns = from.columns;
        this.data = from.data.map( row => new HisSearchResultRow( row, from.columns ) );
        this.offset = from.offset;
        this.limit = from.limit;
        this.maxResultRows = from.maxResultRows;
        this.totalCount = from.totalCount;
    }
}
